import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { productService } from '@/services/product.service';
import { categoryService } from '@/services/category.service';
import { Product } from '@/types/product.types';
import { CategoryWithSubCategories } from '@/types/category.types';
import { formatPrice } from '@/utils/formatters';
import { useCart } from '@/hooks/useCart';
import '@/styles/boutique.css';
import { useTranslation } from 'react-i18next';

export const Boutique = () => {
  const ITEMS_PER_PAGE = 12;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const { addToCart } = useCart();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [categoryTree, setCategoryTree] = useState<CategoryWithSubCategories[]>([]);
  const [maxPrice, setMaxPrice] = useState<number | ''>('');
  const [dateFilter, setDateFilter] = useState<'all' | '30' | '90' | '365'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const [data, cats] = await Promise.all([
          productService.getAll(),
          categoryService.getAll().catch(() => [] as CategoryWithSubCategories[]),
        ]);
        const publishedProducts = data.filter(
          (p) => p.status === 'published' && p.stock_status === 'in-stock'
        );
        setProducts(publishedProducts);
        setCategoryTree(Array.isArray(cats) ? cats : []);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeCategoriesFromApi = useMemo(
    () => categoryTree.filter((c) => c.is_active && c.name?.trim()),
    [categoryTree]
  );

  const categories = useMemo(() => {
    if (activeCategoriesFromApi.length > 0) {
      return [...activeCategoriesFromApi]
        .map((c) => c.name)
        .sort((a, b) => a.localeCompare(b));
    }
    const values = Array.from(new Set(products.map((p) => p.category).filter(Boolean))) as string[];
    return values.sort((a, b) => a.localeCompare(b));
  }, [products, activeCategoriesFromApi]);

  const subCategoryOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    if (activeCategoriesFromApi.length === 0) return opts;

    const parents =
      selectedCategory === 'all'
        ? activeCategoriesFromApi
        : activeCategoriesFromApi.filter((c) => c.name === selectedCategory);

    for (const parent of parents) {
      for (const sub of parent.sub_categories || []) {
        if (!sub.is_active || !sub.name?.trim()) continue;
        const label =
          selectedCategory === 'all'
            ? `${parent.name} — ${sub.name}`
            : sub.name;
        opts.push({ value: sub.name, label });
      }
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [activeCategoriesFromApi, selectedCategory]);

  const maxAvailablePrice = useMemo(() => {
    if (products.length === 0) return 0;
    return Math.ceil(
      Math.max(
        ...products.map((p) => Number(p.reduced_price && p.reduced_price > 0 ? p.reduced_price : p.price) || 0)
      )
    );
  }, [products]);

  const productMatchesCategoryFilters = (product: Product): boolean => {
    const pc = (product.category || '').trim().toLowerCase();
    if (selectedCategory === 'all' && selectedSubCategory === 'all') return true;

    if (selectedSubCategory !== 'all') {
      return pc === selectedSubCategory.trim().toLowerCase();
    }

    if (activeCategoriesFromApi.length > 0) {
      const parent = activeCategoriesFromApi.find((c) => c.name === selectedCategory);
      if (parent) {
        if (pc === parent.name.trim().toLowerCase()) return true;
        for (const sub of parent.sub_categories || []) {
          if (!sub.is_active) continue;
          if (pc === sub.name.trim().toLowerCase()) return true;
        }
        return false;
      }
    }

    return selectedCategory === 'all' || pc === selectedCategory.toLowerCase();
  };

  // Filtrer les produits selon la recherche + filtres gauche
  const filteredProducts = useMemo(() => {
    const query = localSearch.toLowerCase().trim();
    const now = Date.now();
    return products.filter((product) => {
      const name = product.name?.toLowerCase() || '';
      const description = product.description?.toLowerCase() || '';
      const sku = product.sku?.toLowerCase() || '';
      const category = product.category?.toLowerCase() || '';

      const matchesSearch =
        query.length === 0 ||
        name.includes(query) ||
        description.includes(query) ||
        sku.includes(query) ||
        category.includes(query);

      const matchesCategory = productMatchesCategoryFilters(product);

      const currentPrice = Number(
        product.reduced_price && product.reduced_price > 0 ? product.reduced_price : product.price
      );
      const matchesPrice = maxPrice === '' || currentPrice <= Number(maxPrice);

      const productDate = new Date(product.created_at).getTime();
      const days = dateFilter === 'all' ? null : Number(dateFilter);
      const matchesDate = days === null || now - productDate <= days * 24 * 60 * 60 * 1000;

      return matchesSearch && matchesCategory && matchesPrice && matchesDate;
    });
  }, [
    products,
    localSearch,
    selectedCategory,
    selectedSubCategory,
    activeCategoriesFromApi,
    maxPrice,
    dateFilter,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setSelectedSubCategory('all');
  }, [selectedCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [localSearch, selectedCategory, selectedSubCategory, maxPrice, dateFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleAddToCart = async (e: React.MouseEvent, productId: number) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await addToCart(productId, 1);
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/produit-detail?id=${productId}`);
  };

  const normalizeText = (value?: string | null) => {
    if (!value) return '';
    const looksMojibake = /Ã.|Â|â€|â€™|â€œ|â€/.test(value);
    if (!looksMojibake) return value;
    try {
      // Fix common UTF-8 interpreted as Latin-1 issue
      return decodeURIComponent(escape(value));
    } catch {
      return value
        .replace(/Ã©/g, 'é')
        .replace(/Ã¨/g, 'è')
        .replace(/Ãª/g, 'ê')
        .replace(/Ã /g, 'à')
        .replace(/Ã¢/g, 'â')
        .replace(/Ã®/g, 'î')
        .replace(/Ã´/g, 'ô')
        .replace(/Ã»/g, 'û')
        .replace(/Ã§/g, 'ç')
        .replace(/Ã‰/g, 'É');
    }
  };

  return (
    <div className="min-h-screen" style={{ margin: 0, padding: 0, position: 'relative', marginTop: 0, paddingTop: 0 }}>
      <Navbar />
      
      <main className="boutique-page">
        <div className="boutique-container">
          {/* Header */}
          <div className="boutique-header">
            <h1 className="gtitle boutique-title">
              <span className="gtitle-light">{t('boutiquePage.titlePrefix')}</span>
              <span className="gtitle-strong">{t('boutiquePage.titleHighlight')}</span>
            </h1>
            <p className="boutique-subtitle">
              {t('boutiquePage.subtitle')}
            </p>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="boutique-state">
              <p>{t('boutiquePage.loading')}</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="boutique-state">
              {searchQuery ? (
                <div>
                  <p className="boutique-state-message">
                    {t('boutiquePage.noResult', { query: searchQuery })}
                  </p>
                  <button
                    onClick={() => navigate('/boutique')}
                    className="boutique-btn-primary"
                  >
                    {t('boutiquePage.showAll')}
                  </button>
                </div>
              ) : (
                <p className="boutique-state-message">{t('boutiquePage.empty')}</p>
              )}
            </div>
          ) : (
            <div className="boutique-layout">
              <aside className="boutique-filters">
                <h3>{t('boutiquePage.filtersHeading')}</h3>
                <div className="boutique-filter-group">
                  <label htmlFor="boutique-filter-search">{t('boutiquePage.filterSearchLabel')}</label>
                  <input
                    id="boutique-filter-search"
                    type="search"
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder={t('boutiquePage.filterSearchPlaceholder')}
                  />
                </div>
                <div className="boutique-filter-group">
                  <label htmlFor="boutique-filter-category">{t('boutiquePage.filterCategoryLabel')}</label>
                  <select
                    id="boutique-filter-category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">{t('boutiquePage.filterAllCategories')}</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {normalizeText(category)}
                      </option>
                    ))}
                  </select>
                </div>
                {subCategoryOptions.length > 0 && (
                  <div className="boutique-filter-group">
                    <label htmlFor="boutique-filter-subcategory">{t('boutiquePage.filterSubCategoryLabel')}</label>
                    <select
                      id="boutique-filter-subcategory"
                      value={selectedSubCategory}
                      onChange={(e) => setSelectedSubCategory(e.target.value)}
                    >
                      <option value="all">{t('boutiquePage.filterAllSubCategories')}</option>
                      {subCategoryOptions.map((opt) => (
                        <option key={`${opt.value}-${opt.label}`} value={opt.value}>
                          {normalizeText(opt.label)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="boutique-filter-group">
                  <label htmlFor="boutique-filter-price">{t('boutiquePage.filterMaxPrice')}</label>
                  <input
                    id="boutique-filter-price"
                    type="range"
                    min="0"
                    max={Math.max(maxAvailablePrice, 1)}
                    value={maxPrice === '' ? Math.max(maxAvailablePrice, 1) : maxPrice}
                    onChange={(e) => setMaxPrice(Number(e.target.value))}
                  />
                  <div className="boutique-filter-price-value">
                    {maxPrice === '' ? t('boutiquePage.filterAllPrices') : t('boutiquePage.filterMaxPriceHint', { price: formatPrice(Number(maxPrice)) })}
                  </div>
                </div>
                <div className="boutique-filter-group">
                  <label htmlFor="boutique-filter-date">{t('boutiquePage.filterDateLabel')}</label>
                  <select
                    id="boutique-filter-date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value as 'all' | '30' | '90' | '365')}
                  >
                    <option value="all">{t('boutiquePage.filterDateAll')}</option>
                    <option value="30">{t('boutiquePage.filterDate30')}</option>
                    <option value="90">{t('boutiquePage.filterDate90')}</option>
                    <option value="365">{t('boutiquePage.filterDate365')}</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="boutique-btn-primary"
                  onClick={() => {
                    setLocalSearch('');
                    setSelectedCategory('all');
                    setSelectedSubCategory('all');
                    setMaxPrice('');
                    setDateFilter('all');
                  }}
                >
                  {t('boutiquePage.filterReset')}
                </button>
              </aside>

              <div className="boutique-grid-wrap">
                {(localSearch ||
                  selectedCategory !== 'all' ||
                  selectedSubCategory !== 'all' ||
                  maxPrice !== '' ||
                  dateFilter !== 'all') && (
                  <div className="boutique-search-result">
                    <p>{filteredProducts.length} produit(s) trouve(s)</p>
                  </div>
                )}
                <div className="boutique-grid">
                  {paginatedProducts.map((product) => {
                    const finalPrice = product.reduced_price || product.price;
                    const hasDiscount = product.reduced_price && product.reduced_price < product.price;

                    return (
                      <div
                        key={product.id}
                        className="boutique-product-card"
                        onClick={() => handleProductClick(product.id)}
                      >
                        {/* Product Image */}
                        <div className="boutique-product-image-wrapper">
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="boutique-product-image"
                            />
                          ) : (
                            <div className="boutique-product-image-empty">
                              <span>{t('boutiquePage.imageNotAvailable')}</span>
                            </div>
                          )}
                          {hasDiscount && (
                            <div className="boutique-discount-badge">
                              -{Math.round(((product.price - finalPrice) / product.price) * 100)}%
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="boutique-product-content">
                          <h3 className="boutique-product-title">
                            {normalizeText(product.name)}
                          </h3>

                          {product.category && (
                            <p className="boutique-product-category">
                              {normalizeText(product.category)}
                            </p>
                          )}

                          {/* Price */}
                          <div className="boutique-price-row">
                            <span className="boutique-price-current">
                              {formatPrice(finalPrice)}
                            </span>
                            {hasDiscount && (
                              <span className="boutique-price-old">
                                {formatPrice(product.price)}
                              </span>
                            )}
                          </div>

                          {/* Add to Cart Button */}
                          <button
                            className="btn-add-to-cart-boutique w-full px-4 sm:px-6 py-2 sm:py-3 bg-[#8C1843] text-white border-none rounded-lg text-sm sm:text-base md:text-lg font-bold cursor-pointer transition-all duration-300 hover:bg-[#7a1539] hover:-translate-y-0.5 active:translate-y-0"
                            onClick={(e) => handleAddToCart(e, product.id)}
                          >
                            {t('boutiquePage.addToCart')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {filteredProducts.length > ITEMS_PER_PAGE && (
                  <div className="boutique-pagination">
                    <button
                      type="button"
                      className="boutique-page-btn"
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('boutiquePage.paginationPrev')}
                    </button>
                    <span className="boutique-page-indicator">
                      {t('boutiquePage.paginationPageOf', { current: currentPage, total: totalPages })}
                    </span>
                    <button
                      type="button"
                      className="boutique-page-btn"
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('boutiquePage.paginationNext')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

